<template>
  <div class="home">
      <div class="section">

          <section class="container">
              <h4 class="title page-title has-text-centered has-padding-v-50 is-4">Marketplace connection setup</h4>
              <div class="columns">
                <div class="column is-two-thirds">

                  <b-message>
                      This is where you will have access to each step of the integration process for the Marketplace. As each step is ready, you will be given access and instructions.
                  </b-message>

                  <!-- Authentication -->
                  <section class="has-margin-v-50">
                    <div class="step-number is-size-4">1</div>
                    <h4 class="title is-4">Authentication</h4>
                    <p class="subtitle is-6">Choose the authentication method of your connection</p>
                    <b-message type="is-danger" has-icon v-if="!authData">
                      You haven't selected an authentication method yet.<br>
                      <a href="#" @click.prevent="$router.push('/authentication')"
                        class="button">Setup Authentication</a>
                    </b-message>
                    <b-message type="is-success" has-icon v-if="authData">
                      Authentication Type: {{authType}}<br>
                      <a href="#" @click.prevent="$router.push('/authentication')"
                        class="button">Edit Authentication</a>
                    </b-message>
                  </section>

                  <!-- Billing process -->
                  <section class="has-margin-v-50">
                    <div class="step-number">2</div>
                    <h4 class="title is-4">Products &amp; Billing</h4>
                    <p class="subtitle is-6">Set up the billing between supplier (you), the agent and the marketplace</p>
                    <b-message type="is-danger" has-icon v-if="!billingItems.length">
                      You haven't setup your billing products yet.<br>
                      <a href="#" @click.prevent="$router.push('/billing/new')"
                        class="button">Add a Billing Product</a>
                    </b-message>
                    <b-message type="is-success" has-icon v-if="billingItems.length">
                      <b-table :data="billingItems">
                        <template slot-scope="props">
                          <b-table-column field="active" label="Status" width="40">
                            <span class="icon has-text-success" v-if="props.row.active">
                              <i class="fas fa-check"></i>
                            </span>
                            <span class="icon" v-if="!props.row.active" >
                              <i class="fas fa-clock" v-tooltip="'Pending approval'"></i>
                            </span>
                          </b-table-column>
                          <b-table-column field="title" label="Product">
                            <a :href="`/billing/${props.row.itemId}`" @click.prevent="$router.push(`/billing/${props.row.itemId}`)">{{props.row.title}}</a>
                          </b-table-column>
                        </template>
                      </b-table>
                      <a href="#" @click.prevent="$router.push('/billing/new')"
                        class="button">Add a Billing Product</a>
                    </b-message>
                  </section>

                  <!-- Actions -->
                  <section class="has-margin-v-50">
                    <div class="step-number">3</div>
                    <h4 class="title is-4">Your Connections</h4>
                    <p class="subtitle is-6">Send/Receive data to/from BestAgent</p>
                    <b-message type="is-danger" has-icon v-if="!actions.length">
                      You haven't added any connection yet.<br>
                      <a href="#" @click.prevent="$router.push('/actions/add')"
                        class="button">Add Connection</a>
                    </b-message>
                    <b-message type="is-success" has-icon v-if="actions.length">
                      <b-table :data="actions">
                        <template slot-scope="props">
                          <b-table-column field="active" label="Status" width="40">
                            <span class="icon has-text-success" v-if="props.row.tested">
                              <i class="fas fa-check"></i>
                            </span>
                            <span class="icon has-text-danger" v-if="!props.row.tested" >
                              <i class="fas fa-times"></i>
                            </span>
                          </b-table-column>
                          <b-table-column field="title" label="Action">
                            <a :href="`/actions/${props.row.actionId}`" @click.prevent="$router.push(`/actions/${props.row.actionId}`)">{{props.row.type == 1 ? 'SEND' : 'RECEIVE' }} > {{props.row.method}} > {{props.row.action}}</a>
                          </b-table-column>
                        </template>
                      </b-table>
                      <a href="#" @click.prevent="$router.push('/actions/add')"
                        class="button">Add Connection</a>
                    </b-message>
                  </section>

                  <!-- Signup process -->
                  <section class="has-margin-v-50">
                    <div class="step-number">4</div>
                    <h4 class="title is-4">Agent Signup process</h4>
                    <p class="subtitle is-6">Setup how an agent registers for your service</p>
                    <b-message type="is-warning">
                        Pending...
                    </b-message>
                  </section>
                </div>
                <div class="column">
                  <p class="home__add-user__title">Agents</p>
                  <b-table :data="agents">
                    <template slot-scope="props">
                      <b-table-column field="active" label="Live" width="40">
                        <span class="icon has-text-success" v-if="props.row.active == true">
                          <i class="fas fa-check"></i>
                        </span>
                        <span class="icon has-text-danger" v-if="props.row.active == false" >
                          <a class="button is-warning is-small is-rounded" @click.prevent="promptAgentId(props.index)">Activate</a>
                        </span>
                      </b-table-column>
                      <b-table-column field="agentId" label="Agent ID">
                        {{props.row.agentId ? props.row.agentId : '--'}}
                      </b-table-column>
                      <b-table-column field="companyName" label="Company">
                        {{props.row.companyName}}
                      </b-table-column>
                    </template>
                  </b-table>
                  <div v-if="supplier.admin">
                    <p class="home__add-user__title">Add user</p>
                    <b-field class="is-fullwidth">
                        <b-input class="home__add-user__input" placeholder="Email" type="email" v-model="userEmail">
                        </b-input>
                        <p class="control">
                            <button class="button home__add-user__button" @click.prevent="inviteUser">Invite</button>
                        </p>
                    </b-field>
                    <b-notification type="is-success" v-if="showInvitationSuccess">
                      Invitation sent!<br>User will receive an email to sign up on your account.
                    </b-notification>
                    <b-notification type="is-danger" v-if="showInvitationError">
                      Invitation not sent!<br>It seems user already exists or email is not valid.
                    </b-notification>
                    <b-loading :is-full-page="true" :active.sync="invitingUser"></b-loading>
                  </div>
                  <hr v-if="supplier.admin">
                  <div class="user-list" v-if="supplier.admin">
                    <p class="home__add-user__title">Users</p>
                    <b-table :data="supplier.users">
                      <template slot-scope="props" v-if="!me || (me && me.email != props.row.email)">
                        <b-table-column field="email" label="Email">
                          {{props.row.email ? props.row.email : '--'}}
                        </b-table-column>
                        <b-table-column field="name" label="Name">
                          {{props.row.name}}
                        </b-table-column>
                        <b-table-column field="action" label="">
                          <a class="home__add-user__remove" @click.prevent="removeUser(props.row)">Remove</a>
                        </b-table-column>
                      </template>
                    </b-table>

                    <div v-if="supplier.users && supplier.users.length > 1">



                    </div>
                    <div v-else>
                      No users found.
                    </div>
                  </div>
                </div>
              </div>
          </section>
      </div>
  </div>
</template>

<script>
import api from '../libs/api';

export default {
  name: 'home',
  data() {
    return {
      authData: {},
      supplier: {
        users: [],
        company: null,
        authorised: {
          name: '',
          email: '',
          jobTitle: '',
          phone: '',
        },
        commercial: {
          name: '',
          email: '',
          jobTitle: '',
          phone: '',
        },
        technical: {
          name: '',
          email: '',
          jobTitle: '',
          phone: '',
        },
      },
      billingItems: [],
      userEmail: '',
      invitingUser: false,
      showInvitationSuccess: false,
      showInvitationError: false,
      actions: [],
      agents: [],
      me: {}
    };
  },
  async created() {
    const that = this;
    let [billingItems,supplier,authData,actions,agents] = await Promise.all([
      await api({
        endpoint: `${process.env.VUE_APP_API}/suppliers`,
        path: '/billing',
        method: 'POST',
        body: {
          action: 'list'
        }
      }),
      await api({
        endpoint: `${process.env.VUE_APP_API}/suppliers`,
        path: '/info',
        method: 'GET',
      }),
      await api({
        endpoint: `${process.env.VUE_APP_API}/suppliers`,
        path: '/auth',
        method: 'GET',
      }),
      await api({
        endpoint: `${process.env.VUE_APP_API}/suppliers`,
        path: '/actions',
        method: 'POST',
        body: {
          do: 'list'
        }
      }),
      await api({
        endpoint: `${process.env.VUE_APP_API}/suppliers`,
        path: '/agents',
        method: 'GET',
      })
    ]);


    this.billingItems = billingItems;
    this.supplier = supplier;
    this.authData = authData;
    this.actions = actions;
    this.agents = agents;

    this.supplier.users = await this.removeDuplicates(this.supplier.users);
    this.me = this.supplier.users.filter(i => {
      return i.userId == that.supplier.createdBy;
    });
    this.me = this.me[0];

  },
  computed: {
    authType() {
      const authTypes = {
        none: 'No Auth',
        basic: 'Basic Auth',
        token: 'Token',
        apikeys: 'API Keys',
      };
      if (this.authData) {
        return authTypes[this.authData.authType];
      }
      return '';
    },
  },
  methods: {
    async removeDuplicates(arr){
        let unique_array = [];
        let output_array = [];
        await Promise.all(arr.map( (i) => {
          if(unique_array.indexOf(i.email) == -1){
            unique_array.push(i.email);
            output_array.push(i);
          }
        }));
        return output_array
    },
    getValueOrDashes(value) {
      return value || '--';
    },
    async removeUser(userData) {
      const rmv = await api({
        endpoint: `${process.env.VUE_APP_API}/suppliers`,
        path: '/delUser',
        method: 'POST',
        body: {
          email: userData.email,
        },
      });
      this.supplier.users = this.supplier.users.filter(u => {
        return u.email != userData.email
      });
    },
    async inviteUser() {
      this.showInvitationSuccess = false;
      this.showInvitationError = false;
      this.invitingUser = true;

      try {
        const usr = await api({
          endpoint: `${process.env.VUE_APP_API}/suppliers`,
          path: '/invite',
          method: 'POST',
          body: {
            email: this.userEmail,
          },
        });
        if (usr.email) {
          this.supplier.users.push(usr);
          this.userEmail = '';
          this.showInvitationSuccess = true;
        } else {
          this.showInvitationError = true;
        }
      } catch (err) {
        console.log('error inviting',err.message);
        this.showInvitationError = true;
      }
      this.invitingUser = false;
    },
    promptAgentId(index) {
      this.$dialog.prompt({
          message: `What's your company ID for this agent?`,
          inputAttrs: {
            placeholder: 'e.g. 123',
          },
          onConfirm: (value) => this.activateAgent(index, value)
      })
    },
    async activateAgent(index, agentId) {
      const agent = this.agents[index];
      await api({
        endpoint: `${process.env.VUE_APP_API}/suppliers`,
        path: '/agents/activate',
        method: 'POST',
        body: {
          companyId: agent.companyId,
          agentId
        }
      });
      this.agents[index].active = true;
      this.agents[index].agentId = agentId;
    }
  },
};
</script>

<style lang="scss">

// .message-text {
//   line-height: 3rem;
//
//   @media screen and (max-width: 768px) {
//     line-height: normal;
//   }
// }
</style>
